"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";

import { Link } from "@/navigation";

const IMAGE_BASE = "/image/new-version/fabrication/metal-welding/Shop";

const SLIDES = [
	{
		image: `${IMAGE_BASE}/js-precision-metal-welding-machine-shop-1.webp`,
		altKey: "metalWeldingMachineShopSlide1Alt" as const,
	},
	{
		image: `${IMAGE_BASE}/js-precision-metal-welding-machine-shop-2.webp`,
		altKey: "metalWeldingMachineShopSlide2Alt" as const,
	},
	{
		image: `${IMAGE_BASE}/js-precision-metal-welding-machine-shop-3.webp`,
		altKey: "metalWeldingMachineShopSlide3Alt" as const,
	},
	{
		image: `${IMAGE_BASE}/js-precision-metal-welding-machine-shop-4.webp`,
		altKey: "metalWeldingMachineShopSlide4Alt" as const,
	},
	{
		image: `${IMAGE_BASE}/js-precision-metal-welding-machine-shop-5.webp`,
		altKey: "metalWeldingMachineShopSlide5Alt" as const,
	},
] as const;

const GlobeIcon = ({ className }: { className?: string }) => (
	<svg
		className={`h-14 w-14 sm:h-16 sm:w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20 ${className ?? ""}`}
		viewBox="0 0 48 48"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden
	>
		<circle cx="24" cy="24" r="20" />
		<ellipse cx="24" cy="24" rx="20" ry="8" />
		<ellipse cx="24" cy="24" rx="8" ry="20" />
	</svg>
);

export default function MetalWeldingMachineShop() {
	const t = useTranslations("new-welding");
	const [currentIndex, setCurrentIndex] = useState(0);

	const advance = useCallback(() => {
		setCurrentIndex((prev) => (prev + 1) % SLIDES.length);
	}, []);

	useEffect(() => {
		const timer = setInterval(advance, 5000);
		return () => clearInterval(timer);
	}, [advance]);

	return (
		<section className="bg-[#ffffff] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="relative mb-6 flex min-h-[72px] items-center sm:mb-8 sm:min-h-[80px] md:mb-10 md:min-h-[88px] lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						<span className="block">{t("metalWeldingMachineShopTitleLine1")}</span>
						<span className="block">{t("metalWeldingMachineShopTitleLine2")}</span>
					</h2>
					<div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
						<GlobeIcon className="text-themePrimary400" />
					</div>
				</div>

				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-8 xl:gap-10">
					<div className="flex w-full flex-col lg:min-w-0 lg:flex-1">
						<div className="relative aspect-[4/2.8] w-full overflow-hidden bg-white shadow-lg lg:aspect-auto lg:h-full lg:min-h-[380px]">
							{SLIDES.map((slide, index) => (
								<div
									key={slide.image}
									className={`absolute inset-0 transition-opacity duration-500 ${
										index === currentIndex ? "z-10 opacity-100" : "z-0 opacity-0"
									}`}
									aria-hidden={index !== currentIndex}
								>
									<Image
										src={slide.image}
										alt={t(slide.altKey)}
										fill
										className="object-cover"
										sizes="(max-width: 1024px) 100vw, 50vw"
										priority={index === 0}
									/>
								</div>
							))}
						</div>
						<div className="mt-4 flex items-center justify-center gap-2 sm:mt-5">
							{SLIDES.map((_, index) => (
								<button
									key={index}
									type="button"
									onClick={() => setCurrentIndex(index)}
									className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${
										index === currentIndex
											? "bg-themePrimary400"
											: "bg-themePrimary400/25 hover:bg-themePrimary400/40"
									}`}
									aria-label={t("metalWeldingMachineShopCarouselGoToSlide", { slideNumber: index + 1 })}
								/>
							))}
						</div>
					</div>

					<div className="flex w-full min-w-0 flex-1 flex-col justify-start lg:min-w-0">
						<div className="mb-8 space-y-4 text-sm leading-relaxed text-black md:mb-10 md:space-y-5 md:text-base">
							<p>{t("metalWeldingMachineShopParagraph1")}</p>
							<p>{t("metalWeldingMachineShopParagraph2")}</p>
						</div>
						<Link
							href="/upload"
							className="inline-flex w-fit items-center justify-center rounded bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#83b751] hover:text-white md:px-8 md:py-3.5 md:text-base"
						>
							{t("metalWeldingMachineShopGetInstantQuote")}
						</Link>
					</div>
				</div>
			</div>
		</section>
	);
}
